import type { FC } from 'react'

import { Button, Pagination, Skeleton } from 'antd'
import { useLocation, useNavigate, useParams } from 'react-router'

import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { PathUtils } from 'routes/routes.utils'

import type {
  SeriesListCounts,
  SeriesListPage as ListPage,
  SeriesListRow,
} from '../../api/series.types'
import { SchedulingMenu } from '../../components/SchedulingMenu'
import { SERIES_LIST_PER_PAGE } from '../../series.constants'
import { StatsBar, type StatItem } from '../shared/StatsBar'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'
import shared from '../shared/seriesPage.module.scss'
import { SeriesListFilters } from './SeriesListFilters'
import { SeriesListTable } from './SeriesListTable'
import styles from './seriesListPage.module.scss'
import { useCancelSeries } from './useCancelSeries'
import { useSeriesListData } from './useSeriesListData'
import { useSeriesDetails } from './useSeriesDetails'
import { useSeriesListFilters } from './useSeriesListFilters'

const TEXT = SERIES_PAGES_TEXT.list
const SKELETON_ROWS = 5

const toStats = (counts: SeriesListCounts | null): StatItem[] => [
  { label: TEXT.stats.total, value: counts?.total ?? null, tone: 'default' },
  { label: TEXT.stats.active, value: counts?.active ?? null, tone: 'brand' },
  { label: TEXT.stats.completed, value: counts?.completed ?? null, tone: 'active' },
  { label: TEXT.stats.cancelled, value: counts?.cancelled ?? null, tone: 'muted' },
  { label: TEXT.stats.upcomingVisits, value: counts?.upcomingVisits ?? null, tone: 'default' },
]

type ResultsProps = {
  page: ListPage | undefined
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  busyId: number | null
  onCancel: (row: SeriesListRow) => void
  detailsId: number | null
  onDetails: (row: SeriesListRow) => void
}

const Results: FC<ResultsProps> = ({
  page,
  isLoading,
  isError,
  onRetry,
  busyId,
  onCancel,
  detailsId,
  onDetails,
}) => {
  if (isError) {
    return (
      <div className={styles.card}>
        <div className={styles.message} role='alert'>
          {TEXT.error}
          <Button className={styles.button} onClick={onRetry}>
            {TEXT.tryAgain}
          </Button>
        </div>
      </div>
    )
  }
  if (isLoading || !page) {
    return (
      <div className={styles.card} aria-busy='true'>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: SKELETON_ROWS }}
          className={styles.skeleton}
        />
      </div>
    )
  }
  if (page.rows.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.message}>{TEXT.empty}</div>
      </div>
    )
  }
  return (
    <div className={styles.card}>
      <SeriesListTable
        rows={page.rows}
        busyId={busyId}
        detailsId={detailsId}
        onDetails={onDetails}
        onCancel={onCancel}
      />
    </div>
  )
}

export const SeriesListPage: FC = () => {
  const { siteId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { filter, setFilter, reset } = useSeriesListFilters()
  const data = useSeriesListData({ siteId, filter, setFilter })
  const cancel = useCancelSeries(siteId)
  const details = useSeriesDetails({ siteId, onGone: data.refetch })
  const shownRows = data.page?.rows.length ?? 0

  return (
    <section className={shared.page}>
      <header className={styles.header}>
        <h1 className={shared.title}>{TEXT.title}</h1>
        <span className={styles.spacer} />
        <Button
          className={styles.button}
          onClick={() =>
            navigate(PathUtils.getScheduleSeriesTemplates(siteId), {
              state: { listSearch: location.search },
            })
          }>
          {TEXT.manageTemplates}
        </Button>
        <SchedulingMenu current='series' />
      </header>
      <StatsBar items={toStats(data.counts)} />
      <SeriesListFilters
        filter={filter}
        onChange={setFilter}
        onReset={reset}
        providers={data.providers}
        providersState={data.providersState}
      />
      <Results
        page={data.page}
        isLoading={data.isLoading}
        isError={data.isError}
        onRetry={data.refetch}
        busyId={cancel.busyId}
        onCancel={cancel.askCancel}
        detailsId={details.loadingId}
        onDetails={details.openDetails}
      />
      {!data.isError && data.page && shownRows > 0 && (
        <footer className={styles.footer}>
          <span className={styles.showing}>{TEXT.showing(shownRows, data.page.meta.total)}</span>
          <Pagination
            className={styles.pagination}
            size='small'
            current={filter.page}
            pageSize={SERIES_LIST_PER_PAGE}
            total={data.page.meta.total}
            showSizeChanger={false}
            hideOnSinglePage
            onChange={(page) => setFilter({ page })}
          />
        </footer>
      )}
      <Warning {...cancel.warningProps} />
    </section>
  )
}
